import React, { useMemo, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { BarCustomLayer, BarDatum } from "@nivo/bar";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { getChartColors } from "../constants/chartColors";
import { formatNumber } from "../utils/formatNumber";
import { TransformedDataPoint } from "../utils/chartDataTransformers";
import {
  useRevenueProjections,
  RevenueProjectionPoint,
} from "../hooks/useRevenueProjections";
import {
  ProjectionViewMode,
  getMinProjectionPoints,
  PROJECTION_MAX_BAR_WIDTH,
} from "./shared/revenueProjectionsConfig";
import { ToggleOption } from "./shared/ChartHeader";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import ChartToggleButton from "./shared/ChartToggleButton";
import ChartTooltip from "./shared/ChartTooltip";
import { createTrendLineLayer } from "./shared/createTrendLineLayer";
import { createProjectionBarLabelsLayer } from "./shared/createProjectionBarLabelsLayer";

dayjs.extend(quarterOfYear);

const PERIOD_TOGGLE_OPTIONS: ToggleOption[] = [
  { value: "annual", label: "Annual" },
  { value: "quarter", label: "Quarterly" },
];

interface RevenueProjectionsChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  /** Hidden in compact embeds (e.g. AI chat) — the chart keeps only its title + period toggle. */
  showTrendLineToggle?: boolean;
}

const buildProjectionChartData = (
  points: RevenueProjectionPoint[] | undefined,
  viewMode: ProjectionViewMode
): TransformedDataPoint[] => {
  if (!points || points.length === 0) return [];

  return points.map((point) => {
    const date = dayjs(point.date);
    const period =
      viewMode === "annual"
        ? date.year().toString()
        : `Q${date.quarter()} ${date.year()}`;

    return {
      period,
      date: point.date,
      year: date.year(),
      quarter: viewMode === "annual" ? null : date.quarter(),
      revenue: point.revenue / 1_000_000,
      isProjected: point.type === "estimate",
      revenueLow: point.revenueLow,
      revenueHigh: point.revenueHigh,
      numAnalysts: point.numAnalysts,
    };
  });
};

// Constant compound-growth reference line anchored at the latest actual and growing
// at the rate implied across the projection window.
const computeTrend = (chartData: TransformedDataPoint[]) => {
  const values = chartData.map((d) => d.revenue as number);
  if (values.length < 2) {
    return { trendValues: null as number[] | null, growthRate: null as number | null };
  }

  const anchor = values[0];
  const last = values[values.length - 1];
  if (anchor <= 0 || last <= 0) {
    return { trendValues: null, growthRate: null };
  }

  const periods = values.length - 1;
  const growthRate = Math.pow(last / anchor, 1 / periods) - 1;
  const trendValues = values.map((_, index) => anchor * Math.pow(1 + growthRate, index));

  return { trendValues, growthRate };
};

// Period-over-period growth (decimal) for each bar; the anchor has no prior period.
const computeGrowthByIndex = (chartData: TransformedDataPoint[]): (number | null)[] =>
  chartData.map((point, index) => {
    if (index === 0) return null;
    const prev = chartData[index - 1].revenue as number;
    const current = point.revenue as number;
    if (!prev || prev <= 0) return null;
    return (current - prev) / prev;
  });

const RevenueProjectionsChart: React.FC<RevenueProjectionsChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  showTrendLineToggle = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(isDark);

  // Period and trend controls are internal to this chart, independent of the
  // global financials period selector.
  const [viewMode, setViewMode] = useState<ProjectionViewMode>("annual");
  const [showTrend, setShowTrend] = useState(false);

  const { data, isLoading, error } = useRevenueProjections(ticker, viewMode);

  const fullChartData = useMemo(
    () => buildProjectionChartData(data?.points, viewMode),
    [data, viewMode]
  );

  const estimateCount = useMemo(
    () => fullChartData.filter((d) => d.isProjected).length,
    [fullChartData]
  );
  const hasEnoughCoverage = estimateCount >= getMinProjectionPoints(viewMode);

  // When coverage is too thin we render the empty state (the card + header stay so
  // the user can still switch period) rather than a sparse, misleading chart.
  const chartData = hasEnoughCoverage ? fullChartData : [];

  const { trendValues } = useMemo(() => computeTrend(chartData), [chartData]);
  const growthByIndex = useMemo(() => computeGrowthByIndex(chartData), [chartData]);

  const actualColor = colors.positive;
  // Neutral reference-line colour so the dashed trend reads clearly over the green bars.
  const trendColor = isDark ? "#cbd5e1" : "#475569";

  // Projected bars: no fill, green outline + diagonal dashed pattern (a "template" look).
  const projectedDefs = useMemo(
    () => [
      {
        id: "projectedLines",
        type: "patternLines",
        background: "transparent",
        color: colors.positive,
        rotation: -45,
        lineWidth: 2,
        spacing: 5,
      },
    ],
    [colors.positive]
  );
  const projectedFill = useMemo(
    () => [
      {
        // Nivo's fill matcher receives the computed bar node, so the raw datum
        // (and our isProjected flag) sits at node.data.data.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (d: any) => Boolean(d?.data?.data?.isProjected),
        id: "projectedLines",
      },
    ],
    []
  );

  const labelsLayer = useMemo(
    () =>
      createProjectionBarLabelsLayer({
        formatRevenue: (millions) => `${currencySymbol}${formatNumber(millions * 1_000_000)}`,
        growthByIndex,
        revenueColor: theme.palette.text.primary,
        growthPositiveColor: colors.positive,
        growthNegativeColor: colors.negative,
        isModal,
      }),
    [currencySymbol, growthByIndex, theme.palette.text.primary, colors.positive, colors.negative, isModal]
  );

  const extraLayers = useMemo(() => {
    const layers: BarCustomLayer<BarDatum>[] = [];
    if (showTrend && trendValues) {
      layers.push(createTrendLineLayer({ trendValues, color: trendColor, isModal }));
    }
    if (!isMobile) {
      layers.push(labelsLayer);
    }
    return layers;
  }, [showTrend, trendValues, trendColor, isModal, isMobile, labelsLayer]);

  const trendToggle = (
    <ChartToggleButton
      label="Trend line"
      selected={showTrend}
      onToggle={() => setShowTrend((value) => !value)}
      icon={<ShowChartIcon sx={{ fontSize: 16 }} />}
    />
  );

  return (
    <GenericFinancialBarChart
      title="Revenue Projections"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode={viewMode === "annual" ? "annual" : "quarterly"}
      barKeys={["revenue"]}
      colors={(bar) => (bar.data.isProjected ? "transparent" : actualColor)}
      borderWidth={1.5}
      borderColor={colors.positive}
      defs={projectedDefs}
      fill={projectedFill}
      yAxisLegend="Revenue"
      yAxisFormat={(value) => `${currencySymbol}${formatNumber(value * 1_000_000)}`}
      maxBarWidth={PROJECTION_MAX_BAR_WIDTH}
      hideValueLabels
      bottomMargin={isMobile ? undefined : 86}
      axisBottomTickPadding={isMobile ? undefined : 32}
      extraLayers={extraLayers}
      keepHeaderWhenEmpty
      headerToggleOptions={PERIOD_TOGGLE_OPTIONS}
      headerToggleValue={viewMode}
      onHeaderToggleChange={(value) => setViewMode(value as ProjectionViewMode)}
      noDataTitle="Limited Analyst Coverage"
      noDataDescription={`There aren't enough ${
        viewMode === "annual" ? "annual" : "quarterly"
      } revenue estimates for this company.`}
      tooltipContent={(bar) => {
        const point = bar.data as TransformedDataPoint;
        const isProjected = Boolean(point.isProjected);

        const rows = [
          {
            label: isProjected ? "Est. Revenue" : "Revenue",
            value: `${currencySymbol}${formatNumber((bar.value as number) * 1_000_000, 2)}`,
          },
        ];

        if (isProjected) {
          const low = point.revenueLow as number | null;
          const high = point.revenueHigh as number | null;
          if (low != null && high != null) {
            rows.push({
              label: "Range",
              value: `${currencySymbol}${formatNumber(low)} – ${currencySymbol}${formatNumber(high)}`,
            });
          }
          const numAnalysts = point.numAnalysts as number | null;
          if (numAnalysts != null) {
            rows.push({ label: "Analysts", value: String(numAnalysts) });
          }
        }

        return (
          <ChartTooltip header={String(bar.indexValue)} headerColor={bar.color} rows={rows} />
        );
      }}
      belowChartContent={
        showTrendLineToggle && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
            {trendToggle}
          </Box>
        )
      }
    />
  );
};

export default RevenueProjectionsChart;
